import { IDealersCurrency } from "@/types";
import { formatDealerAmount } from "@/utils/currency";

const DEFAULT_DEALERS_CURRENCY: IDealersCurrency = {
  dogma_name: 'British Pound',
  dogma_vatpercentage: 0,
  dogma_currencysymbol: '£',
  dogma_exchangerate: 1,
  dogma_isocurrencycode: 'GBP',
};

/**
 * Hook that provides currency conversion utilities based on the account's
 * configured dealers currency (dogma_DealersCurrency).
 *
 * - All prices stored and sent to the backend remain in GBP.
 * - This hook converts GBP amounts to the display currency for UI rendering only.
 */
export const useCurrency = () => {
  const dealersCurrency: IDealersCurrency = DEFAULT_DEALERS_CURRENCY;

  const displayCurrency = dealersCurrency.dogma_isocurrencycode;
  const exchangeRate = dealersCurrency.dogma_exchangerate;

  /** Convert a GBP amount to the account's display currency */
  const convertFromGBP = (gbpAmount: number): number => {
    if (exchangeRate === 1) return gbpAmount;
    return gbpAmount * exchangeRate;
  };

  /** Convert a GBP amount and return a formatted display string (respects currencyprecision) */
  const formatPrice = (gbpAmount: number): string => {
    return formatDealerAmount(gbpAmount, dealersCurrency, exchangeRate);
  };

  /**
   * Format using the account currency's symbol/precision but an explicit exchange rate override.
   * Useful when displaying per-group totals where each group may have a different rate.
   */
  const formatPriceWithRate = (gbpAmount: number, overrideRate: number): string => {
    return formatDealerAmount(gbpAmount, dealersCurrency, overrideRate);
  };

  return { displayCurrency, exchangeRate, convertFromGBP, formatPrice, formatPriceWithRate, dealersCurrency };
};
